import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Target, TrendingUp, RefreshCw, Loader2,
  Phone, AlertTriangle, CheckCircle2, XCircle, BookOpen,
  MessageSquare, Activity, Calendar
} from 'lucide-react';
import { getUserKPI, getUserKPIHistory, recalculateUserKPI } from '@/lib/api';
import { toast } from 'sonner';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const getScoreColor = (score) => {
  if (score >= 8) return 'text-emerald-600';
  if (score >= 6) return 'text-blue-600';
  if (score >= 4) return 'text-amber-600';
  return 'text-red-600';
};

const getScoreBgColor = (score) => {
  if (score >= 8) return 'bg-emerald-500';
  if (score >= 6) return 'bg-blue-500';
  if (score >= 4) return 'bg-amber-500';
  return 'bg-red-500';
};

export default function UserKPI() {
  const { user } = useOutletContext();
  const [isLoading, setIsLoading] = useState(true);
  const [isRecalculating, setIsRecalculating] = useState(false);
  
  const [kpiData, setKpiData] = useState(null);
  const [kpiHistory, setKpiHistory] = useState([]);
  
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  useEffect(() => {
    if (user?.id) {
      loadKPIData();
      loadKPIHistory();
    }
  }, [user?.id, selectedMonth, selectedYear]);

  const loadKPIData = async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const res = await getUserKPI(user.id, selectedMonth, selectedYear);
      setKpiData(res.data);
    } catch (error) {
      console.error('Failed to load KPI data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadKPIHistory = async () => {
    if (!user?.id) return;
    try {
      const res = await getUserKPIHistory(user.id, 12);
      setKpiHistory(res.data);
    } catch (error) {
      console.error('Failed to load KPI history:', error);
    }
  };

  const handleRecalculate = async () => {
    if (!user?.id) return;
    setIsRecalculating(true);
    try {
      const res = await recalculateUserKPI(user.id, selectedMonth, selectedYear);
      setKpiData(res.data);
      toast.success('KPI recalculated successfully');
      loadKPIHistory();
    } catch (error) {
      toast.error('Failed to recalculate KPI');
    } finally {
      setIsRecalculating(false);
    }
  };

  const generateYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear - 1, currentYear, currentYear + 1];
  };

  if (isLoading && !kpiData) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-kpi-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-card/20 rounded-lg">
              <Target className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">My KPI Score</h1>
              <p className="text-purple-100 mt-1">
                Performance metrics based on Call Quality, Homework, Feedback & Engagement
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Select 
              value={String(selectedMonth)} 
              onValueChange={(v) => setSelectedMonth(parseInt(v))}
            >
              <SelectTrigger className="w-[130px] bg-card/20 border-white/30 text-white">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select 
              value={String(selectedYear)} 
              onValueChange={(v) => setSelectedYear(parseInt(v))}
            >
              <SelectTrigger className="w-[90px] bg-card/20 border-white/30 text-white">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {generateYearOptions().map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant="secondary" 
              size="icon"
              onClick={handleRecalculate}
              disabled={isRecalculating}
              className="bg-card/20 hover:bg-card/30 text-white"
            >
              {isRecalculating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Score Card */}
      {kpiData && (
        <Card className={`${kpiData.has_compliance_violation ? 'border-red-300 bg-red-50' : 'border-purple-200'}`}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="text-center flex-1">
                <p className="text-sm text-muted-foreground mb-2">
                  {MONTHS[selectedMonth - 1]} {selectedYear} KPI Score
                </p>
                <div className="relative inline-flex items-center justify-center">
                  <svg className="w-32 h-32 transform -rotate-90">
                    <circle
                      className="text-slate-200"
                      strokeWidth="10"
                      stroke="currentColor"
                      fill="transparent"
                      r="52"
                      cx="64"
                      cy="64"
                    />
                    <circle
                      className={kpiData.total_kpi_score >= 8 ? 'text-emerald-500' : 
                                 kpiData.total_kpi_score >= 6 ? 'text-blue-500' :
                                 kpiData.total_kpi_score >= 4 ? 'text-amber-500' : 'text-red-500'}
                      strokeWidth="10"
                      strokeDasharray={`${(kpiData.total_kpi_score / 10) * 327} 327`}
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="52"
                      cx="64"
                      cy="64"
                    />
                  </svg>
                  <span className={`absolute text-3xl font-bold ${getScoreColor(kpiData.total_kpi_score)}`}>
                    {kpiData.total_kpi_score}
                  </span>
                </div>
                <p className="text-muted-foreground mt-2">out of 10</p>
              </div>
              
              <div className="flex-1 space-y-4">
                {/* Score Breakdown Mini */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-blue-500" />
                    <span className="text-sm flex-1">Call Quality (70%)</span>
                    <span className={`font-bold ${getScoreColor(kpiData.call_quality_score / 0.7)}`}>
                      {kpiData.call_quality_score}/7
                    </span>
                  </div>
                  <Progress value={(kpiData.call_quality_score / 7) * 100} className="h-2" />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-green-500" />
                    <span className="text-sm flex-1">Homework (10%)</span>
                    <span className={`font-bold ${getScoreColor(kpiData.homework_completion_score * 10)}`}>
                      {kpiData.homework_completion_score}/1
                    </span>
                  </div>
                  <Progress value={kpiData.homework_completion_score * 100} className="h-2" />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-amber-500" />
                    <span className="text-sm flex-1">Feedback (10%)</span>
                    <span className={`font-bold ${getScoreColor(kpiData.feedback_compliance_score * 10)}`}>
                      {kpiData.feedback_compliance_score}/1
                    </span>
                  </div>
                  <Progress value={kpiData.feedback_compliance_score * 100} className="h-2" />
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Activity className="h-4 w-4 text-purple-500" />
                    <span className="text-sm flex-1">Engagement (10%)</span>
                    <span className={`font-bold ${getScoreColor(kpiData.coaching_engagement_score * 10)}`}>
                      {kpiData.coaching_engagement_score}/1
                    </span>
                  </div>
                  <Progress value={kpiData.coaching_engagement_score * 100} className="h-2" />
                </div>
              </div>
            </div>
            
            {/* Compliance Violation Warning */}
            {kpiData.has_compliance_violation && (
              <div className="mt-4 p-3 bg-red-100 border border-red-300 rounded-lg">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="font-medium">Compliance Violation Detected</span>
                </div>
                <p className="text-sm text-red-600 mt-1">
                  Your KPI score has been set to 0 due to compliance issues this month.
                </p>
                {kpiData.compliance_violations?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-red-600">
                    {kpiData.compliance_violations.map((v, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        {v}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Detailed Breakdown */}
      {kpiData && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Call Quality Details */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Phone className="h-4 w-4 text-blue-500" />
                Call Quality Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Average Call Score</span>
                  <span className="font-medium">{kpiData.call_quality_details?.avg_score || 0}/10</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Calls</span>
                  <span className="font-medium">{kpiData.call_quality_details?.total_calls || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Best Calls</span>
                  <span className="font-medium text-emerald-600">{kpiData.call_quality_details?.best_calls || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Good Calls</span>
                  <span className="font-medium text-blue-600">{kpiData.call_quality_details?.good_calls || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Improvement Needed</span>
                  <span className="font-medium text-amber-600">{kpiData.call_quality_details?.improvement_calls || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">High Alert</span>
                  <span className="font-medium text-red-600">{kpiData.call_quality_details?.high_alert_calls || 0}</span>
                </div>
                {kpiData.call_quality_details?.note && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-red-600">{kpiData.call_quality_details.note}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Homework Details */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-green-500" />
                Homework Completion Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Tasks Assigned</span>
                  <span className="font-medium">{kpiData.homework_completion_details?.total_tasks || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tasks Completed</span>
                  <span className="font-medium text-emerald-600">{kpiData.homework_completion_details?.completed_tasks || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Completion Rate</span>
                  <span className="font-medium">{kpiData.homework_completion_details?.completion_rate || 0}%</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Points Earned</span>
                    <Badge className={getScoreBgColor(kpiData.homework_completion_score * 10)}>
                      {kpiData.homework_completion_score}/1
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Feedback Details */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-amber-500" />
                Feedback Compliance Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Call Logs</span>
                  <span className="font-medium">{kpiData.feedback_compliance_details?.total_logs || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Logs with Notes</span>
                  <span className="font-medium text-emerald-600">{kpiData.feedback_compliance_details?.with_notes || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Compliance Rate</span>
                  <span className="font-medium">{kpiData.feedback_compliance_details?.compliance_rate || 0}%</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Points Earned</span>
                    <Badge className={getScoreBgColor(kpiData.feedback_compliance_score * 10)}>
                      {kpiData.feedback_compliance_score}/1
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Engagement Details */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="h-4 w-4 text-purple-500" />
                Coaching Engagement Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Activities</span>
                  <span className="font-medium">{kpiData.coaching_engagement_details?.total_activities || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Library Views</span>
                  <span className="font-medium">{kpiData.coaching_engagement_details?.library_views || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Library Listens</span>
                  <span className="font-medium">{kpiData.coaching_engagement_details?.library_listens || 0}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Engagement Rate</span>
                  <span className="font-medium">{kpiData.coaching_engagement_details?.engagement_rate || 0}%</span>
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Points Earned</span>
                    <Badge className={getScoreBgColor(kpiData.coaching_engagement_score * 10)}>
                      {kpiData.coaching_engagement_score}/1
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* KPI History */}
      {kpiHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-muted-foreground" />
              KPI History
            </CardTitle>
            <CardDescription>Your KPI performance over the past months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {kpiHistory.map((kpi) => (
                <div 
                  key={`${kpi.year}-${kpi.month}`}
                  className={`flex-shrink-0 w-24 p-3 rounded-lg text-center cursor-pointer transition-all ${
                    kpi.month === selectedMonth && kpi.year === selectedYear 
                      ? 'bg-purple-100 border-2 border-purple-500' 
                      : 'bg-muted/50 hover:bg-muted'
                  }`}
                  onClick={() => {
                    setSelectedMonth(kpi.month);
                    setSelectedYear(kpi.year);
                  }}
                >
                  <p className="text-xs text-muted-foreground">
                    {MONTHS[kpi.month - 1].substring(0, 3)} {kpi.year}
                  </p>
                  <p className={`text-2xl font-bold ${getScoreColor(kpi.total_kpi_score)}`}>
                    {kpi.total_kpi_score}
                  </p>
                  {kpi.has_compliance_violation && (
                    <AlertTriangle className="h-3 w-3 text-red-500 mx-auto mt-1" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tips to Improve */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <TrendingUp className="h-5 w-5" />
            Tips to Improve Your KPI
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-blue-600" />
              <span><strong>Call Quality (70%):</strong> Focus on achieving scores of 6+ on all calls. Score = (avg_score / 10) × 7. Below 6 = 0 points.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-blue-600" />
              <span><strong>Homework (10%):</strong> Complete all assigned coaching tasks on time with quality submissions.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-blue-600" />
              <span><strong>Feedback (10%):</strong> Add detailed notes to all your call logs to maintain compliance.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 mt-0.5 text-blue-600" />
              <span><strong>Engagement (10%):</strong> Regularly visit the Library, listen to best practice calls, and engage with coaching materials.</span>
            </li>
            <li className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600" />
              <span><strong>Important:</strong> Any compliance or guideline violation will result in a KPI score of 0 for the entire month.</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
